import React, { useState, useEffect } from 'react';

// Import components
import LoginView from './components/views/LoginView';
import StudentAccessPrompt from './components/views/StudentAccessPrompt';
import HomeView from './components/views/HomeView';
import FinanceView from './components/views/FinanceView';
import AcademicView from './components/views/AcademicView';
import ReportViewWithTabs from './components/views/ReportViewWithTabs';
import RewardManagementView from './components/views/RewardManagementView';
import RecordDetailView from './components/views/RecordDetailView';
import RecordFormView from './components/forms/RecordFormView';
import FinanceFormView from './components/forms/FinanceFormView';
import StudentFormView from './components/forms/StudentFormView';
import CourseFormView from './components/forms/CourseFormView';
import SearchOverlay from './components/layout/SearchOverlay';
import FooterNav from './components/layout/FooterNav';
import ToastContainer from './components/ui/ToastContainer';
import VoidRecordDialog from './components/dialogs/VoidRecordDialog';
import AccessTokenModal from './components/modals/AccessTokenModal';

// Import permission store and services
import usePermissionStore from './stores/permissionStore';
import { authService, studentService, courseService, purchaseService, attendanceService, apiClient } from './services';
import { useToast } from './hooks/useToast';

export default function App() {
  // Permission store
  const { isLoggedIn, login, logout, role } = usePermissionStore();
  const toast = useToast();

  // State management
  const [currentView, setCurrentView] = useState(isLoggedIn ? 'home' : 'login');
  const [activeTab, setActiveTab] = useState('home');
  const [academicTab, setAcademicTab] = useState('students');
  
  const [selectedRecord, setSelectedRecord] = useState(null);
  const [selectedStudent, setSelectedStudent] = useState(null);
  const [selectedCourse, setSelectedCourse] = useState(null);
  const [selectedFinance, setSelectedFinance] = useState(null);
  const [isEditing, setIsEditing] = useState(false);
  
  const [isSearchOpen, setIsSearchOpen] = useState(false);
  const [searchContext, setSearchContext] = useState('record');
  const [searchParams, setSearchParams] = useState({});
  const [searchTrigger, setSearchTrigger] = useState(0);
  // 学生快速筛选：当前激活的学生姓名（null 表示未筛选）
  const [activeStudentFilter, setActiveStudentFilter] = useState(null);
  
  // 防重入锁（同步）+ UI 状态（异步，仅用于按钮 disabled/文案展示）
  // ⚠️ 见 docs/ASYNC_LOCK_PATTERN.md —— 锁必须用 useRef，不能用 useState
  const isSavingRef = React.useRef(false);
  const [isSaving, setIsSaving] = useState(false);

  // 作废对话框状态
  const [isVoidDialogOpen, setIsVoidDialogOpen] = useState(false);
  const [voidRecordData, setVoidRecordData] = useState(null);

  // 分享链接模态框状态
  const [shareStudent, setShareStudent] = useState(null);

  // 直接复制分享链接 - 现已修改为通过模态框提供一致且在 iOS/微信端 100% 成功的分享体验
  const handleShareCopyLink = (studentData) => {
    if (!studentData) return;
    setShareStudent(studentData);
  };

  // 处理快捷令牌路由 (免密登录)
  useEffect(() => {
    const handleTokenLogin = async () => {
      const path = window.location.pathname;
      if (path.startsWith('/token/')) {
        const token = path.split('/token/')[1];
        if (token) {
          try {
            console.log('正在执行免密登录...', token);
            const res = await authService.tokenLogin(token);
            if (res.user) {
              // 登录成功，更新权限商店
              login(res.user);
              
              // 如果返回了 student_id，则设置它
              if (res.student_id) {
                const { setStudentId } = usePermissionStore.getState();
                setStudentId(res.student_id);
              }
              
              toast.success(`欢迎回来，${res.user.username}`);
              
              // 延迟一秒跳转到主页，让用户看到提示
              setTimeout(() => {
                window.location.href = '/';
              }, 1000);
            }
          } catch (err) {
            console.error('免密登录失败:', err);
            toast.error('访问链接已失效');
            // 跳转到普通登录页
            setTimeout(() => {
              window.location.href = '/login';
            }, 2000);
          }
        }
      }
    };
    
    handleTokenLogin();
  }, []);

  // 学生姓名快速筛选（在上课记录列表点击学生姓名触发）
  const handleStudentFilter = (studentName) => {
    if (!studentName) {
      // 清除筛选：恢复默认状态（仅显示 ACTIVE 记录）
      setActiveStudentFilter(null);
      setSearchParams({});
      setSearchTrigger(prev => prev + 1);
    } else {
      // 激活筛选：按学生姓名，显示全部状态记录
      setActiveStudentFilter(studentName);
      setSearchParams({ student_name: studentName, status: 'all' });
      setSearchTrigger(prev => prev + 1);
    }
  };

  // 处理搜索
  const handleSearch = (params) => {
    console.log('搜索参数:', params);
    // 如果用户主动搜索，清除学生快速筛选状态
    setActiveStudentFilter(null);
    setSearchParams(params);
    setSearchTrigger(prev => prev + 1); // 触发搜索
  };

  // 监听登录状态变化
  useEffect(() => {
    if (!isLoggedIn && currentView !== 'login') {
      setCurrentView('login');
      setActiveTab('home');
    }
  }, [isLoggedIn, currentView]);

  // 监听角色变化，确保在角色改变时正确跳转
  useEffect(() => {
    // 如果是学生角色，显示访问受限提示，不再自动跳转
    if (isLoggedIn && role === 'student' && currentView !== 'student_access_prompt') {
      console.log('检测到学生角色，显示访问受限提示...');
      setCurrentView('student_access_prompt');
      return;
    }

    if (isLoggedIn && role !== 'student' && currentView === 'student_home') {
      setCurrentView('home');
      setActiveTab('home');
    }
  }, [isLoggedIn, role, currentView]);

  // 订阅 store 的登出事件
  useEffect(() => {
    const unsubscribe = usePermissionStore.subscribe(
      (state, prevState) => {
        // 当从登录状态变为未登录状态时
        if (prevState.isLoggedIn && !state.isLoggedIn) {
          setCurrentView('login');
          setActiveTab('home');
        }
      }
    );
    return unsubscribe;
  }, []);

  // Navigation handlers
  const handleLogin = () => {
    // 获取最新的角色状态，避免闭包中的旧状态导致角色切换时跳转错误
    const currentRole = usePermissionStore.getState().role;
    
    // 根据用户角色决定跳转到哪个页面
    if (currentRole === 'student') {
      // 学生角色跳转到 i.sumarts.cn 登录页
      console.log('学生角色登录，跳转到 i.sumarts.cn 登录页');
      window.location.href = 'https://i.sumarts.cn/login';
    } else {
      setCurrentView('home');
      setActiveTab('home');
    }
  };

  const handleRecordClick = (record) => {
    setSelectedRecord(record);
    setIsEditing(false);
    setCurrentView('record_detail');
  };

  const handleEditStudent = async (student) => {
    try {
      // 获取学生详细信息（包含用户账号信息）
      const studentDetail = await studentService.getById(student.id);
      setSelectedStudent(studentDetail);
      setCurrentView('student_edit');
    } catch (error) {
      console.error('获取学生详情失败:', error);
      // 降级处理：使用列表中的数据
      setSelectedStudent(student);
      setCurrentView('student_edit');
    }
  };

  const handleEditCourse = (course) => {
    setSelectedCourse(course);
    setCurrentView('course_edit');
  };

  const handleFinanceClick = (finance) => {
    setSelectedFinance(finance);
    setCurrentView('finance_edit');
  };

  const handleTabChange = (tab) => {
    setActiveTab(tab);
    setSearchParams({}); // Clear search params on tab change to prevent cross-tab parameter pollution
    setSearchTrigger(0); // Reset search trigger
    setActiveStudentFilter(null); // 切换 Tab 时清除学生筛选
    if (tab === 'home') setCurrentView('home');
    if (tab === 'academic') setCurrentView('academic');
    if (tab === 'finance') setCurrentView('finance');
    if (tab === 'report') setCurrentView('report');
    if (tab === 'reward') setCurrentView('reward');
  };

  const handleAcademicTabChange = (tab) => {
    setAcademicTab(tab);
    setSearchParams({}); // Clear search params on sub-tab change to prevent pollution between student and course searches
    setSearchTrigger(0); // Reset search trigger
  };

  const openSearch = (context) => {
    setSearchContext(context);
    setIsSearchOpen(true);
  };

  // Render current view
  const renderView = () => {
    switch (currentView) {
      case 'login':
        return <LoginView onLogin={handleLogin} />;
        
      case 'student_access_prompt':
        return <StudentAccessPrompt />;
      
      case 'home':
        return (
          <HomeView
            searchParams={searchParams}
            searchTrigger={searchTrigger}
            onRecordClick={handleRecordClick}
            onSearch={openSearch}
            onAddRecord={() => {
              setSelectedRecord(null);
              setCurrentView('record_add');
            }}
            onTokenAccess={handleShareCopyLink}
            onStudentFilter={handleStudentFilter}
            activeStudentFilter={activeStudentFilter}
          />
        );
      
      case 'academic':
        return (
          <AcademicView
            searchParams={searchParams}
            searchTrigger={searchTrigger}
            academicTab={academicTab}
            setAcademicTab={handleAcademicTabChange}
            onSearch={openSearch}
            onAddItem={() => setCurrentView(academicTab === 'students' ? 'student_add' : 'course_add')}
            onEditStudent={handleEditStudent}
            onTokenAccess={handleShareCopyLink}
            onEditCourse={handleEditCourse}
            onShare={handleShareCopyLink}
          />
        );
      
      case 'finance':
        return (
          <FinanceView
            searchParams={searchParams}
            searchTrigger={searchTrigger}
            onFinanceClick={handleFinanceClick}
            onSearch={openSearch}
            onAddFinance={() => {
              setSelectedFinance(null);
              setCurrentView('finance_add');
            }}
          />
        );
      
      case 'finance_add':
        return (
          <FinanceFormView
            editMode={false}
            isSaving={isSaving}
            onBack={() => setCurrentView('finance')}
            onSave={async (formData) => {
              if (isSavingRef.current) return;
              isSavingRef.current = true;
              setIsSaving(true);
              try {
                console.log('创建财务记录:', formData);
                await purchaseService.create(formData);
                toast.success('财务记录创建成功！');
                setCurrentView('finance');
              } catch (error) {
                console.error('创建财务记录失败:', error);
                toast.error('创建失败：' + (error.message || '未知错误'));
              } finally {
                isSavingRef.current = false;
                setIsSaving(false);
              }
            }}
            onDelete={() => setCurrentView('finance')}
          />
        );
      
      case 'finance_edit':
        return (
          <FinanceFormView
            editMode={true}
            initialData={selectedFinance}
            isSaving={isSaving}
            onBack={() => setCurrentView('finance')}
            onSave={async (formData) => {
              if (isSavingRef.current) return;
              isSavingRef.current = true;
              setIsSaving(true);
              try {
                console.log('更新财务记录:', formData);
                await purchaseService.update(formData.id, formData);
                toast.success('财务记录更新成功！');
                setCurrentView('finance');
              } catch (error) {
                console.error('更新财务记录失败:', error);
                toast.error('更新失败：' + (error.message || '未知错误'));
              } finally {
                isSavingRef.current = false;
                setIsSaving(false);
              }
            }}
            onDelete={async () => {
              // 获取完整的购买记录详情（包含剩余课时信息）
              try {
                const recordDetail = await purchaseService.getById(selectedFinance.id);
                setVoidRecordData(recordDetail);
                setIsVoidDialogOpen(true);
              } catch (error) {
                console.error('获取记录详情失败:', error);
                toast.error('获取记录信息失败：' + (error.message || '未知错误'));
              }
            }}
          />
        );
      
      case 'record_detail':
        return (
          <RecordDetailView
            record={selectedRecord}
            editMode={false}
            onBack={() => {
              setCurrentView('home');
            }}
            onEdit={() => {
              setIsEditing(true);
              setCurrentView('record_edit');
            }}
            onShare={(record) => {
              handleShareCopyLink({ id: record.student_id, name: record.student_name });
            }}
            onSave={(updatedData) => {
              console.log('保存记录更新:', updatedData);
              // TODO: 调用 API 更新记录
              setIsEditing(false);
              toast.success('修改已保存！');
              setCurrentView('record_detail');
            }}
            onDelete={() => {
              toast.success('记录删除成功！');
              setCurrentView('home');
            }}
          />
        );
      
      case 'record_add':
        return (
          <RecordFormView
            editMode={false}
            isSaving={isSaving}
            onBack={() => {
              setCurrentView('home');
            }}
            onSave={async (formData) => {
              if (isSavingRef.current) return;
              isSavingRef.current = true;
              setIsSaving(true);
              try {
                console.log('创建上课记录:', formData);
                await attendanceService.create(formData);
                toast.success('上课记录创建成功！');
                setCurrentView('home');
              } catch (error) {
                console.error('创建上课记录失败:', error);
                toast.error(error.response?.data?.error || '创建上课记录失败');
              } finally {
                isSavingRef.current = false;
                setIsSaving(false);
              }
            }}
          />
        );
      
      case 'record_edit':
        return (
          <RecordFormView
            editMode={true}
            initialData={selectedRecord}
            isSaving={isSaving}
            onBack={() => setCurrentView('record_detail')}
            onSave={async (formData) => {
              if (isSavingRef.current) return;
              isSavingRef.current = true;
              setIsSaving(true);
              try {
                console.log('更新上课记录:', formData);
                await attendanceService.update(formData.id, formData);
                toast.success('修改已保存！');
                
                // 刷新记录数据
                const updatedRecord = await attendanceService.getById(formData.id);
                setSelectedRecord(updatedRecord);
                
                setCurrentView('record_detail');
              } catch (error) {
                console.error('更新上课记录失败:', error);
                toast.error(error.response?.data?.error || '更新上课记录失败');
              } finally {
                isSavingRef.current = false;
                setIsSaving(false);
              }
            }}
            onDelete={async () => {
              try {
                console.log('作废上课记录:', selectedRecord.id);
                await attendanceService.void(selectedRecord.id);
                toast.success('记录已作废，课时已退回！');
                setCurrentView('home');
              } catch (error) {
                console.error('作废上课记录失败:', error);
                toast.error(error.response?.data?.error || '作废上课记录失败');
              }
            }}
          />
        );
      
      case 'student_add':
        return (
          <StudentFormView
            editMode={false}
            isSaving={isSaving}
            onBack={() => setCurrentView('academic')}
            onSave={async (formData) => {
              if (isSavingRef.current) return;
              isSavingRef.current = true;
              setIsSaving(true);
              try {
                console.log('创建学生:', formData);
                await studentService.create(formData);
                toast.success('学生创建成功！');
                setCurrentView('academic');
              } catch (error) {
                console.error('创建学生失败:', error);
                toast.error('创建失败：' + (error.message || '未知错误'));
              } finally {
                isSavingRef.current = false;
                setIsSaving(false);
              }
            }}
          />
        );
      
      case 'student_edit':
        return (
          <StudentFormView
            editMode={true}
            initialData={selectedStudent}
            isSaving={isSaving}
            onBack={() => setCurrentView('academic')}
            onSave={async (formData) => {
              if (isSavingRef.current) return;
              isSavingRef.current = true;
              setIsSaving(true);
              try {
                console.log('更新学生:', formData);
                await studentService.update(formData.id, formData);
                toast.success('学生信息更新成功！');
                setCurrentView('academic');
              } catch (error) {
                console.error('更新学生失败:', error);
                toast.error('更新失败：' + (error.message || '未知错误'));
              } finally {
                isSavingRef.current = false;
                setIsSaving(false);
              }
            }}
            onTokenAccess={() => handleShareCopyLink(selectedStudent)}
          />
        );
      
      case 'course_add':
        return (
          <CourseFormView
            editMode={false}
            isSaving={isSaving}
            onBack={() => setCurrentView('academic')}
            onSave={async (formData) => {
              if (isSavingRef.current) return;
              isSavingRef.current = true;
              setIsSaving(true);
              try {
                console.log('创建课程:', formData);
                await courseService.create(formData);
                toast.success('课程创建成功！');
                setCurrentView('academic');
              } catch (error) {
                console.error('创建课程失败:', error);
                toast.error('创建失败：' + (error.message || '未知错误'));
              } finally {
                isSavingRef.current = false;
                setIsSaving(false);
              }
            }}
          />
        );
      
      case 'course_edit':
        return (
          <CourseFormView
            editMode={true}
            initialData={selectedCourse}
            isSaving={isSaving}
            onBack={() => setCurrentView('academic')}
            onSave={async (formData) => {
              if (isSavingRef.current) return;
              isSavingRef.current = true;
              setIsSaving(true);
              try {
                console.log('更新课程:', formData);
                await courseService.update(formData.id, formData);
                toast.success('课程信息更新成功！');
                setCurrentView('academic');
              } catch (error) {
                console.error('更新课程失败:', error);
                toast.error('更新失败：' + (error.message || '未知错误'));
              } finally {
                isSavingRef.current = false;
                setIsSaving(false);
              }
            }}
          />
        );
      
      case 'report':
        return (
          <ReportViewWithTabs
            searchParams={searchParams}
            searchTrigger={searchTrigger}
            onRecordClick={handleRecordClick}
            onSearch={openSearch}
            onTokenAccess={handleShareCopyLink}
          />
        );
      
      case 'reward':
        return (
          <RewardManagementView />
        );
      
      default:
        return <LoginView onLogin={handleLogin} />;
    }
  };

  const showFooterNav = ['home', 'academic', 'finance', 'report', 'reward'].includes(currentView);
  
  // 开发环境使用手机模拟器，生产环境直接显示
  const isDevelopment = import.meta.env.DEV;

  // 生产环境：直接渲染移动端视图
  if (!isDevelopment) {
    return (
      <div className="h-screen bg-white font-sans flex flex-col overflow-hidden">
        {/* 搜索覆盖层 */}
        <SearchOverlay 
          isOpen={isSearchOpen}
          onClose={() => setIsSearchOpen(false)}
          searchContext={searchContext}
          onSearch={handleSearch}
        />
        
        {/* 主内容区域 - 自动计算高度，减去底部导航 */}
        <div className="flex-1 overflow-hidden">
          {renderView()}
        </div>
        
        {/* 底部导航 - 固定在底部 */}
        {showFooterNav && (
          <div className="fixed bottom-0 left-0 right-0 z-50">
            <FooterNav activeTab={activeTab} onTabChange={handleTabChange} />
          </div>
        )}
        
        {/* 作废记录对话框 */}
        <VoidRecordDialog
          isOpen={isVoidDialogOpen}
          record={voidRecordData}
          remainingHours={voidRecordData?.remaining_hours || 0}
          remainingAmount={voidRecordData?.remaining_amount || 0}
          onConfirm={async ({ reason, proof_urls }) => {
            try {
              console.log('作废财务记录:', { reason, proof_urls });
              await purchaseService.voidRecord(selectedFinance.id, reason, proof_urls);
              toast.success('财务记录已作废，对冲单已生成！');
              setIsVoidDialogOpen(false);
              setVoidRecordData(null);
              setCurrentView('finance');
            } catch (error) {
              console.error('作废财务记录失败:', error);
              toast.error('作废失败：' + (error.message || '未知错误'));
            }
          }}
          onCancel={() => {
            setIsVoidDialogOpen(false);
            setVoidRecordData(null);
          }}
        />

        {/* 快速分享模态框 */}
        {shareStudent && (
          <AccessTokenModal
            student={shareStudent}
            onClose={() => setShareStudent(null)}
          />
        )}
        
        {/* Toast 容器 */}
        <ToastContainer />
      </div>
    );
  }

  // 开发环境：显示手机模拟器
  return (
    <div className="min-h-screen bg-gray-200 flex items-center justify-center p-4 font-sans">
      {/* Device Frame - 仅开发环境 */}
      <div 
        className="relative bg-white shadow-2xl overflow-hidden ring-8 ring-gray-900 ring-opacity-5"
        style={{ width: '375px', height: '812px', borderRadius: '40px' }}
      >
        {/* Status Bar */}
        {currentView !== 'login' && (
          <div className="h-[44px] w-full bg-white flex items-center justify-between px-6 z-50 absolute top-0 left-0">
            <span className="text-xs font-bold text-gray-800">14:30</span>
            <div className="flex gap-1.5 items-center">
              <div className="w-4 h-2.5 bg-gray-800 rounded-[1px]"></div>
              <div className="w-0.5 h-1 bg-gray-800"></div>
            </div>
          </div>
        )}

        {/* View Content Area */}
        <div className={`h-full flex flex-col relative ${currentView !== 'login' ? 'pt-[44px]' : ''}`}>
          <SearchOverlay 
            isOpen={isSearchOpen}
            onClose={() => setIsSearchOpen(false)}
            searchContext={searchContext}
            onSearch={handleSearch}
          />
          
          <div className="flex-1 overflow-hidden relative">
            {renderView()}
          </div>
          
          {showFooterNav && (
            <FooterNav activeTab={activeTab} onTabChange={handleTabChange} />
          )}
        </div>
        
        {/* Home Indicator */}
        <div className="absolute bottom-1 left-1/2 transform -translate-x-1/2 w-32 h-1 bg-gray-900 rounded-full opacity-20 pointer-events-none"></div>
        
        {/* 作废记录对话框 - 在模拟器容器内 */}
        <VoidRecordDialog
          isOpen={isVoidDialogOpen}
          record={voidRecordData}
          remainingHours={voidRecordData?.remaining_hours || 0}
          remainingAmount={voidRecordData?.remaining_amount || 0}
          onConfirm={async ({ reason, proof_urls }) => {
            try {
              console.log('作废财务记录:', { reason, proof_urls });
              // 传递原因和凭证URLs，由后端根据数据库实际剩余额度决定作废金额和课时
              await purchaseService.voidRecord(selectedFinance.id, reason, proof_urls);
              toast.success('财务记录已作废，对冲单已生成！');
              setIsVoidDialogOpen(false);
              setVoidRecordData(null);
              setCurrentView('finance');
            } catch (error) {
              console.error('作废财务记录失败:', error);
              console.log('错误详情 - message:', error.message);
              console.log('错误详情 - data:', error.data);
              console.log('错误详情 - status:', error.status);
              
              toast.error('作废失败：' + (error.message || '未知错误'));
            }
          }}
          onCancel={() => {
            setIsVoidDialogOpen(false);
            setVoidRecordData(null);
          }}
        />

        {/* 快速分享模态框 */}
        {shareStudent && (
          <AccessTokenModal
            student={shareStudent}
            onClose={() => setShareStudent(null)}
          />
        )}
        
        {/* Toast 容器 - 在模拟器容器内 */}
        <ToastContainer />
      </div>
    </div>
  );
}

